import React from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle2, Save, Send, Upload, Paperclip, Trash2, File as FileIcon } from 'lucide-react';
import { intake as intakeApi } from '@/lib/api';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function PortalIntakePage() {
  const { token } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [answers, setAnswers] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    intakeApi.portalLoad(token)
      .then((d) => { setData(d); setAnswers(d.submission?.answers || {}); })
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const save = async () => {
    setSaving(true);
    try { await intakeApi.portalSave(token, answers); toast.success('Progress saved'); }
    catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    // Validate required
    const missing = [];
    (data?.template?.sections || []).forEach((s) => {
      (s.fields || []).forEach((f) => {
        if (f.required && f.type !== 'section_header') {
          const v = answers[f.key];
          if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) missing.push(f.label);
        }
      });
    });
    if (missing.length) { toast.error(`Missing required: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '…' : ''}`); return; }
    setSubmitting(true);
    try { await intakeApi.portalSubmit(token, answers); setDone(true); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-[#2ECC9A]" /></div>;
  if (error) return <div className="flex min-h-screen items-center justify-center bg-slate-50 p-8"><Card><CardContent className="p-8 text-center"><div className="text-lg font-semibold text-red-600">{error}</div><div className="mt-2 text-sm text-slate-500">Please contact your migration agent for a new link.</div></CardContent></Card></div>;
  if (done) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-8" data-testid="intake-portal-done">
      <Card className="max-w-md"><CardContent className="p-10 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-emerald-500" />
        <div className="font-serif text-2xl font-bold text-[#1A3A5C]">Thanks {data?.submission?.applicant_name}!</div>
        <p className="mt-3 text-sm text-slate-600">Your intake has been received. Your migration agent will review your answers and be in touch shortly.</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="intake-portal-page">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6">
          <h1 className="font-serif text-3xl font-bold text-[#1A3A5C]">{data.template.name}</h1>
          {data.template.subclass && <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">Subclass {data.template.subclass}</div>}
          {data.template.description && <p className="mt-3 text-sm text-slate-600">{data.template.description}</p>}
          <p className="mt-3 text-sm text-slate-500">Hi {data.submission.applicant_name}, please answer the questions below. You can save your progress anytime and return via the same link.</p>
        </div>

        {(data.template.sections || []).map((sec, si) => (
          <Card key={si} className="mb-4" data-testid={`intake-portal-section-${si}`}>
            <CardContent className="p-6">
              <h2 className="mb-4 font-serif text-lg font-semibold text-[#1A3A5C]">{sec.title}</h2>
              <div className="space-y-4">
                {(sec.fields || []).map((f, fi) => (
                  <FieldRenderer key={fi} field={f} value={answers[f.key]} onChange={(v) => set(f.key, v)} testid={`intake-portal-field-${f.key}`} />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        <DocVault token={token} />

        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={save} disabled={saving} data-testid="intake-portal-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save progress
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="intake-portal-submit">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Submit
          </Button>
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, testid }) {
  if (field.type === 'section_header') return <div className="text-sm font-semibold uppercase tracking-wider text-slate-500">{field.label}</div>;
  const label = (
    <label className="mb-1 block text-sm font-medium text-slate-700">
      {field.label} {field.required && <span className="text-red-500">*</span>}
      {field.help && <div className="text-xs font-normal text-slate-500">{field.help}</div>}
    </label>
  );
  switch (field.type) {
    case 'textarea':
      return <div>{label}<Textarea rows={4} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case 'select':
      return <div>{label}<Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>{(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select></div>;
    case 'multiselect':
      return <div>{label}<div className="space-y-1">
        {(field.options || []).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={(value || []).includes(o)} onChange={(e) => {
              const arr = value || [];
              onChange(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o));
            }} data-testid={`${testid}-${o}`} /> {o}
          </label>
        ))}
      </div></div>;
    case 'checkbox':
      return <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />{field.label}{field.required && <span className="text-red-500">*</span>}</label>;
    case 'date':
      return <div>{label}<Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case 'number':
      return <div>{label}<Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case 'email':
      return <div>{label}<Input type="email" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case 'phone':
      return <div>{label}<Input type="tel" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
    case 'file':
      return <div>{label}<Input type="file" onChange={(e) => onChange(e.target.files?.[0]?.name || '')} data-testid={testid} /><div className="mt-1 text-xs text-slate-500">(For sensitive documents, upload securely via the client portal after submission.)</div></div>;
    case 'address':
      return <div>{label}<Textarea rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Street, City, State, Postcode, Country" data-testid={testid} /></div>;
    default:
      return <div>{label}<Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid} /></div>;
  }
}


const ACCEPTED_LABELS = ['Passport', 'Payslip', 'Bank statement', 'ID', 'Skills assessment', 'Reference letter', 'Other'];

function DocVault({ token }) {
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [dragging, setDragging] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [label, setLabel] = React.useState('Passport');
  const inputRef = React.useRef(null);

  const load = React.useCallback(() => {
    axios.get(`${API_BASE}/intake/portal/${token}/docs`)
      .then((r) => setDocs(r.data.items || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [token]);

  React.useEffect(load, [load]);

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error('Max 15 MB per file'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (label) fd.append('label', label);
    try {
      await axios.post(`${API_BASE}/intake/portal/${token}/docs`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} uploaded`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Remove this file?')) return;
    try {
      await axios.delete(`${API_BASE}/intake/portal/${token}/docs/${id}`);
      toast.success('Removed'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    files.forEach((f) => doUpload(f));
  };

  return (
    <Card className="mb-4" data-testid="intake-portal-doc-vault">
      <CardContent className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-slate-500" />
          <h2 className="font-serif text-lg font-semibold text-[#1A3A5C]">Supporting documents</h2>
        </div>
        <p className="mb-4 text-xs text-slate-500">Attach copies of your passport, payslips, ID and any evidence documents. Files are encrypted at rest and only visible to your migration agent.</p>

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Document type</label>
            <Select value={label} onValueChange={setLabel}>
              <SelectTrigger data-testid="intake-portal-doc-label"><SelectValue /></SelectTrigger>
              <SelectContent>{ACCEPTED_LABELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="intake-portal-doc-pick">
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Choose file
          </Button>
          <input ref={inputRef} type="file" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="intake-portal-doc-input" />
        </div>

        <div
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`mb-4 rounded-lg border-2 border-dashed p-6 text-center transition ${dragging ? 'border-emerald-400 bg-emerald-50/60' : 'border-slate-300 bg-slate-50/40'}`}
          data-testid="intake-portal-doc-dropzone"
        >
          <Upload className="mx-auto mb-2 h-8 w-8 text-slate-400" />
          <div className="text-sm text-slate-600">Drop files here or use <button onClick={() => inputRef.current?.click()} className="font-semibold text-emerald-700 hover:underline">Choose file</button></div>
          <div className="mt-1 text-xs text-slate-400">Max 15 MB · PDF, images, docs</div>
        </div>

        {loading ? <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#2ECC9A]" /></div>
         : docs.length === 0 ? <div className="text-center text-xs text-slate-400">No files uploaded yet.</div>
         : <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3" data-testid={`intake-portal-doc-${d.id}`}>
                <div className="flex items-center gap-3">
                  <FileIcon className="h-4 w-4 text-slate-500" />
                  <div>
                    <div className="text-sm font-medium text-[#1A3A5C]">{d.file_name}</div>
                    <div className="text-xs text-slate-500">
                      {d.label && <Badge className="mr-2 border border-slate-200 bg-slate-50 text-slate-600">{d.label}</Badge>}
                      {(d.size_bytes / 1024).toFixed(1)} KB · {new Date(d.uploaded_at).toLocaleString('en-AU')}
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => doDelete(d.id)} data-testid={`intake-portal-doc-del-${d.id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
              </div>
            ))}
           </div>
        }
      </CardContent>
    </Card>
  );
}
